<script setup>
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { computed, reactive } from "vue";
import { useUserStore } from "@/Stores/userStore";
import InputPrompt from "@/Components/InputPrompt.vue";
import ItemThumbnail from "@/Components/ItemThumbnail.vue";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import PermissionConstants from "@/Constants/permission-constants";
import { getEquipmentImageUrl, shouldUsePlaceholder } from "@/Helpers/EquipmentPlaceholderHelper.js";
import { VALIDATION_TYPES } from "@/Helpers/ValidationHelper.js";
import { getCategoryLabel, getCurrentEditionProperties } from "@/Helpers/EquipmentPropertiesHelper.js";

const emits = defineEmits(['close', 'updated', 'show-delete-confirmation'])
const userStore = useUserStore();

const props = defineProps({
    item: {
        type: Object,
        default: null
    },
});

const data = reactive({
    selectedPropertyKey: null,
    selectedPropertyValue: null,
    selectedPropertyIndex: null,
    isEditingEquipment: false,
    clearedFields: [],
})

function updateProperty(key) {
    data.selectedPropertyKey = key;
}

function applyChanges() {
    const updateData = {
        item: props.item,
        clearedFields: data.clearedFields
    };
    emits('updated', updateData);
    data.clearedFields = []; // Reset cleared fields after applying
}

function handleUpdated() {
    data.selectedPropertyKey = null;
    applyChanges();
}

function showDeleteConfirmation() {
    emits('show-delete-confirmation', props.item)
    emits('close')
}

const isEquipmentAuthor = computed(() => {
    return props.item?.user_id === userStore.getUserIdFromToken();
})

const isAdmin = computed(() => {
    return !!userStore.hasPermission(PermissionConstants.permissions.admin);
})

function hasMagicEffects(item) {
    if (item.magic_effects != null && item.magic_effects.length > 0) {
        return true;
    }
    return false
}

function hasProperties(item) {
    if (item.properties != null && item.properties.length > 0) {
        return true;
    }
    return false
}

function toggleEdit() {
    data.isEditingEquipment = !data.isEditingEquipment;
}

function editProperty(key, value, index = null) {
    data.selectedPropertyKey = key;
    data.selectedPropertyValue = value;
    data.selectedPropertyIndex = index;
}

function getValidationTypeForField(fieldName) {
    const diceFields = ['damage', 'damage_dice', 'heal_dice'];
    const numberFields = ['armor', 'armor_class', 'duration', 'charges'];

    if (diceFields.includes(fieldName)) {
        return VALIDATION_TYPES.DICE;
    } else if (numberFields.includes(fieldName)) {
        return VALIDATION_TYPES.NUMBER;
    }
    return VALIDATION_TYPES.TEXT;
}

function getMinValueForField(fieldName) {
    const fieldsWithMinimum = ['armor', 'armor_class', 'duration', 'charges'];
    return fieldsWithMinimum.includes(fieldName) ? 0 : null;
}

// Default to 5E if edition is not specified or not recognized
const currentEditionProperties = computed(() => {
    return getCurrentEditionProperties(props.item);
})

// Properties shown in the top section next to image
const topSectionProperties = ['name', 'category', 'cost', 'weight', 'damage', 'damage_dice', 'heal_dice', 'armor']

const displayedProperties = computed(() => {
    return Object.entries(currentEditionProperties.value).filter(([key, config]) => {
        // Skip admin-only properties for non-admins
        if (config.adminOnly && !isAdmin.value) return false;
        // Show property if it has a value
        return props.item?.[key] != null && props.item[key] !== '';
    });
})

// Properties for the top section (excluding description)
const topProperties = computed(() => {
    return displayedProperties.value.filter(([key]) =>
        topSectionProperties.includes(key) && key !== 'description'
    );
})

// Properties for the detailed section (excluding those shown in top and description)
const detailedProperties = computed(() => {
    return displayedProperties.value.filter(([key]) =>
        !topSectionProperties.includes(key) && key !== 'description'
    );
})

const availableToAdd = computed(() => {
    return Object.entries(currentEditionProperties.value).filter(([key, config]) => {
        // Skip admin-only properties for non-admins
        if (config.adminOnly && !isAdmin.value) return false;
        // Skip if property already has a value
        return props.item?.[key] == null || props.item[key] === '';
    });
})

function addProperty(key) {
    if (props.item) {
        props.item[key] = '';
        editProperty(key, '', null);
    }
}

function handleClear(clearEvent) {
    const fieldKey = clearEvent.key || data.selectedPropertyKey;
    if (fieldKey && !data.clearedFields.includes(fieldKey)) {
        data.clearedFields.push(fieldKey);
    }
    data.selectedPropertyKey = null;
    applyChanges();
}

</script>

<template>
    <TransitionRoot as="template" :show="item != null">
        <Dialog as="div" class="relative z-40" @close="$emit('close')">
            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                <div class="fixed inset-0 bg-gray-800 bg-opacity-75 transition-opacity" />
            </TransitionChild>

            <!-- Input Prompt Modal (inside main dialog) -->
            <InputPrompt
                v-if="data.selectedPropertyKey && isEquipmentAuthor"
                :property-key="data.selectedPropertyKey"
                :item="item"
                :validation-type="getValidationTypeForField(data.selectedPropertyKey)"
                :min-value="getMinValueForField(data.selectedPropertyKey)"
                :show-clear="true"
                @close="data.selectedPropertyKey = null"
                @updated="handleUpdated"
                @clear="handleClear"
            />

            <div class="mt-0 sm:mt-8 fixed inset-0 z-10 overflow-y-auto">
                <div class="flex min-h-full items-start justify-center text-center sm:items-center sm:p-4 p-0">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                        <DialogPanel>
                            <div class="flex flex-col w-full sm:w-[48rem] max-h-[85vh] overflow-y-auto bg-gray-750 rounded-lg shadow-lg">
                                <!-- Header -->
                                <div class="text-left text-white p-4 border-b border-gray-700 bg-gray-700 sticky top-0 z-20">
                                    <div class="flex justify-between items-center">
                                        <h2 class="text-2xl font-bold flex items-center">
                                            <FontAwesomeIcon :icon="['far', 'sword']" class="mr-3" />
                                            {{ item?.name || 'Equipment' }}
                                        </h2>
                                        <div class="flex items-center gap-3 ml-4">
                                            <!-- Edit Toggle -->
                                            <FontAwesomeIcon
                                                v-if="isEquipmentAuthor"
                                                :icon="['fas', 'edit']"
                                                :class="data.isEditingEquipment ? 'text-blue-600' : 'text-blue-400'"
                                                class="cursor-pointer hover:text-blue-500 transition-colors"
                                                size="lg"
                                                @click="toggleEdit"
                                                title="Toggle Edit Mode"
                                            />
                                            <!-- Delete -->
                                            <FontAwesomeIcon
                                                v-if="isEquipmentAuthor"
                                                :icon="['fas', 'trash']"
                                                class="cursor-pointer text-red-400 hover:text-red-300 transition-colors"
                                                size="lg"
                                                @click="showDeleteConfirmation"
                                                title="Delete Equipment"
                                            />
                                            <!-- Close -->
                                            <FontAwesomeIcon
                                                :icon="['fas', 'times']"
                                                class="cursor-pointer text-gray-400 hover:text-white transition-colors"
                                                size="lg"
                                                @click="$emit('close')"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <!-- Content -->
                                <div class="bg-gray-900 overflow-y-auto text-left">
                                    <!-- Equipment Image & Basic Info -->
                                    <div class="p-6 border-b border-gray-700">
                                        <div class="flex gap-6">
                                            <!-- Image -->
                                            <div class="flex-shrink-0">
                                                <ItemThumbnail
                                                    :image-url="getEquipmentImageUrl(item)"
                                                    :image-description="item?.image_description"
                                                    :width="'12rem'"
                                                    :height="'12rem'"
                                                    :immediate-load="true"
                                                    @image-selected="!shouldUsePlaceholder(item) ? $emit('image-selected', $event) : null"
                                                    class="rounded-lg"
                                                />
                                            </div>

                                            <!-- Basic Properties (excluding description) -->
                                            <div class="flex-1 space-y-3">
                                                <div v-for="[key, config] in topProperties" :key="key" class="flex items-center justify-between">
                                                    <div class="flex items-center">
                                                        <FontAwesomeIcon
                                                            v-if="isEquipmentAuthor && data.isEditingEquipment"
                                                            :icon="['fas', 'edit']"
                                                            class="mr-2 text-blue-600 cursor-pointer hover:text-blue-500 transition-colors"
                                                            size="sm"
                                                            @click="editProperty(key, item[key])"
                                                        />
                                                        <FontAwesomeIcon
                                                            :icon="[config.iconSet || 'fas', config.icon]"
                                                            :class="`mr-2 ${config.color || 'text-gray-400'}`"
                                                            size="sm"
                                                        />
                                                        <span class="text-gray-300 font-medium">{{ config.label }}:</span>
                                                    </div>
                                                    <span class="text-white ml-4 flex-1">
                                                        <span v-if="key === 'category'">{{ getCategoryLabel(item[key]) || 'N/A' }}</span>
                                                        <span v-else>{{ item[key] || 'N/A' }}</span>
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Description Section -->
                                    <div v-if="item?.description" class="px-6 py-4 border-b border-gray-700">
                                        <div class="flex items-start">
                                            <FontAwesomeIcon
                                                v-if="isEquipmentAuthor && data.isEditingEquipment"
                                                :icon="['fas', 'edit']"
                                                class="mr-3 mt-1 text-blue-600 cursor-pointer hover:text-blue-500 transition-colors"
                                                size="sm"
                                                @click="editProperty('description', item.description)"
                                            />
                                            <div class="flex-1">
                                                <h3 class="text-md font-semibold text-white mb-2 flex items-center">
                                                    <FontAwesomeIcon :icon="['fas', 'align-left']" class="mr-2 text-cyan-400" />
                                                    Description
                                                </h3>
                                                <p class="text-gray-300 leading-relaxed">{{ item.description }}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Detailed Properties -->
                                    <div class="p-6 space-y-4">

                                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div v-for="[key, config] in detailedProperties" :key="key" class="bg-gray-800 rounded-lg p-4">
                                                <div class="flex items-start justify-between">
                                                    <div class="flex items-center mb-2">
                                                        <FontAwesomeIcon
                                                            v-if="isEquipmentAuthor && data.isEditingEquipment"
                                                            :icon="['fas', 'edit']"
                                                            class="mr-2 text-blue-600 cursor-pointer hover:text-blue-500 transition-colors"
                                                            size="sm"
                                                            @click="editProperty(key, item[key])"
                                                        />
                                                        <FontAwesomeIcon
                                                            :icon="[config.iconSet || 'fas', config.icon]"
                                                            :class="`mr-2 ${config.color || 'text-gray-400'}`"
                                                            size="sm"
                                                        />
                                                        <span class="text-gray-300 font-medium">{{ config.label }}</span>
                                                    </div>
                                                </div>
                                                <div class="text-white">
                                                    <span v-if="item[key]">
                                                        <span v-if="key === 'category'">{{ getCategoryLabel(item[key]) }}</span>
                                                        <span v-else>{{ item[key] }}</span>
                                                    </span>
                                                    <span v-else class="text-gray-500 italic">Not set</span>
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Add Properties -->
                                        <div v-if="isEquipmentAuthor && data.isEditingEquipment && availableToAdd.length > 0" class="mt-6">
                                            <h4 class="text-md font-semibold text-white mb-3 flex items-center">
                                                <FontAwesomeIcon :icon="['fas', 'plus']" class="mr-2" />
                                                Add Properties
                                            </h4>
                                            <div class="flex flex-wrap gap-2">
                                                <button
                                                    v-for="[key, config] in availableToAdd"
                                                    :key="key"
                                                    @click="addProperty(key)"
                                                    class="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-400 bg-gray-800 border border-blue-400 rounded-md hover:bg-blue-400 hover:text-white transition-colors"
                                                >
                                                    <FontAwesomeIcon
                                                        :icon="[config.iconSet || 'fas', config.icon]"
                                                        class="mr-1"
                                                        size="sm"
                                                    />
                                                    {{ config.label }}
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
